import React, { useEffect, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './PnLDashboard.css';

interface BalanceData {
  date: string;
  portfolio_balance: number;
  cash_balance: number;
}

interface BalanceHistoryResponse {
  balance_history: BalanceData[];
}

const PnLDashboard: React.FC = () => {
  const { user } = useAuth();
  const [balanceData, setBalanceData] = useState<BalanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pnl, setPnl] = useState<number>(0);
  const [pnlPercentage, setPnlPercentage] = useState<number>(0);

  useEffect(() => {
    const fetchBalanceHistory = async () => {
      if (!user?.issuer) return;

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/user/balance-history?user_issuer=${user.issuer}&days=30`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error('Failed to fetch balance history');

        const data: BalanceHistoryResponse = await response.json();
        const history = data.balance_history || [];
        
        setBalanceData(history);

        // Calculate PnL from first to last entry
        if (history.length > 0) {
          const firstBalance = history[0].portfolio_balance;
          const lastBalance = history[history.length - 1].portfolio_balance;
          const pnlValue = lastBalance - firstBalance;
          const pnlPercent = firstBalance > 0 ? (pnlValue / firstBalance) * 100 : 0;
          
          setPnl(pnlValue);
          setPnlPercentage(pnlPercent);
        }

        setError(null);
      } catch (err) {
        console.error('Failed to load balance history:', err);
        setError('Failed to load balance history');
      } finally {
        setLoading(false);
      }
    };

    fetchBalanceHistory();
  }, [user?.issuer]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTooltipValue = (value: number) => {
    return formatCurrency(value);
  };

  if (loading) {
    return <div className="pnl-dashboard-loading">Loading PnL data...</div>;
  }

  if (error) {
    return <div className="pnl-dashboard-error">{error}</div>;
  }

  if (balanceData.length === 0) {
    return (
      <div className="pnl-dashboard-empty">
        <h2 className="section-title">Profit/Loss Tracking</h2>
        <div className="empty-state">
          <p>Profit/Loss tracking starts today. Check back tomorrow to see your performance!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pnl-dashboard">
      <h2 className="section-title">Profit / Loss Tracking</h2>

      {/* PnL Overview */}
      <div className="pnl-overview">
        <div className="pnl-card">
          <div className="pnl-label">Total P&L</div>
          <div className={`pnl-value ${pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : 'neutral'}`}>
            {formatCurrency(pnl)}
          </div>
          <div className={`pnl-percentage ${pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : 'neutral'}`}>
            {pnlPercentage > 0 ? '+' : ''}{pnlPercentage.toFixed(2)}%
          </div>
        </div>
        <div className="pnl-card">
          <div className="pnl-label">Current Balance</div>
          <div className="pnl-value neutral">
            {formatCurrency(balanceData[balanceData.length - 1]?.portfolio_balance || 0)}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <h3 className="chart-title">Portfolio Balance Over Time</h3>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={balanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                stroke="#999"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#666' }}
              />
              <YAxis 
                tickFormatter={(value) => {
                  if (value >= 1000000) return `$${(value/1000000).toFixed(1)}M`;
                  if (value >= 1000) return `$${(value/1000).toFixed(0)}K`;
                  return `$${value.toFixed(0)}`;
                }}
                stroke="#999"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#666' }}
                width={60}
              />
              <Tooltip 
                formatter={(value: number) => [formatTooltipValue(value), 'Portfolio Balance']}
                labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="portfolio_balance" 
                stroke="#FF3100" 
                strokeWidth={2}
                dot={{ fill: '#FF3100', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#FF3100', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default PnLDashboard;
